import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Users,
  UserPlus,
  UserCheck,
  TrendingUp,
  BarChart3,
  PieChart,
  RefreshCw,
  Calendar,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Upload,
  FileSpreadsheet,
  X,
  Database,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const formatNumber = (num) => {
  return new Intl.NumberFormat("pt-BR").format(num || 0);
};

const formatPercent = (num) => {
  return `${(num || 0).toFixed(1)}%`;
};

// ==================== COMPONENTES ====================

function StatCard({ title, value, subtitle, icon: Icon, color, trend, trendValue }) {
  return (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className={cn("p-2 rounded-lg", color)}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          {trend && (
            <div className={cn(
              "flex items-center text-xs font-medium",
              trend === "up" ? "text-green-600" : "text-red-600"
            )}>
              {trend === "up" ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
              {trendValue}
            </div>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-gray-500">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function InstituicaoBar({ instituicao, base, externos, internos, total, maxTotal }) {
  const percentBase = total > 0 ? (base / total) * 100 : 0;
  const percentExterno = total > 0 ? (externos / total) * 100 : 0;
  const percentInterno = total > 0 ? (internos / total) * 100 : 0;
  const barWidth = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
  
  // Extrair nome curto da instituição (remover código)
  const nomeInstituicao = instituicao.includes(" - ") 
    ? instituicao.split(" - ").slice(1).join(" - ")
    : instituicao;

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-40 text-sm font-medium truncate" title={instituicao}>{nomeInstituicao || "Não Informado"}</div>
      <div className="flex-1">
        <div className="h-6 bg-gray-100 rounded-full overflow-hidden flex" style={{ width: `${Math.max(barWidth, 10)}%`, minWidth: "60px" }}>
          <div 
            className="h-full bg-gray-400 flex items-center justify-center text-xs text-white font-medium"
            style={{ width: `${percentBase}%` }}
            title={`Base: ${base}`}
          >
            {base > 0 && base}
          </div>
          <div 
            className="h-full bg-orange-500 flex items-center justify-center text-xs text-white font-medium"
            style={{ width: `${percentExterno}%` }}
            title={`Externos: ${externos}`}
          >
            {externos > 0 && externos}
          </div>
          <div 
            className="h-full bg-blue-500 flex items-center justify-center text-xs text-white font-medium"
            style={{ width: `${percentInterno}%` }}
            title={`Internos: ${internos}`}
          >
            {internos > 0 && internos}
          </div>
        </div>
      </div>
      <div className="w-16 text-right text-sm font-semibold">{formatNumber(total)}</div>
    </div>
  );
}

function CanalProspeccaoBar({ canal, externos, internos, total, maxTotal }) {
  const percentExterno = total > 0 ? (externos / total) * 100 : 0;
  const percentInterno = total > 0 ? (internos / total) * 100 : 0;
  const barWidth = maxTotal > 0 ? (total / maxTotal) * 100 : 0;

  // Cores específicas para canais de prospecção
  const canalColors = {
    "WHATSAPP": { bg: "bg-green-500", text: "text-green-700" },
    "LIGAÇÃO CELULAR": { bg: "bg-blue-500", text: "text-blue-700" },
    "LIGAÇÃO FIXO": { bg: "bg-blue-600", text: "text-blue-800" },
    "INSTAGRAM": { bg: "bg-pink-500", text: "text-pink-700" },
    "FACEBOOK": { bg: "bg-indigo-500", text: "text-indigo-700" },
    "E-MAIL MKT": { bg: "bg-yellow-500", text: "text-yellow-700" },
    "SMS MKT": { bg: "bg-purple-500", text: "text-purple-700" },
    "PABX MKT": { bg: "bg-cyan-500", text: "text-cyan-700" },
    "SITE": { bg: "bg-teal-500", text: "text-teal-700" },
    "INDICAÇÃO": { bg: "bg-amber-500", text: "text-amber-700" },
    "PRESENCIAL": { bg: "bg-emerald-500", text: "text-emerald-700" },
  };
  const color = canalColors[canal]?.bg || "bg-gray-500";

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-32 text-sm font-medium truncate" title={canal}>{canal || "Não Informado"}</div>
      <div className="flex-1">
        <div className="h-6 bg-gray-100 rounded-full overflow-hidden flex" style={{ width: `${Math.max(barWidth, 15)}%`, minWidth: "80px" }}>
          <div 
            className={`h-full ${color} flex items-center justify-center text-xs text-white font-medium`}
            style={{ width: "100%" }}
            title={`Total: ${total} (Ext: ${externos}, Int: ${internos})`}
          >
            {total > 0 && total}
          </div>
        </div>
      </div>
      <div className="w-20 text-right">
        <span className="text-xs text-orange-600 font-medium">{externos}</span>
        <span className="text-xs text-gray-400 mx-1">/</span>
        <span className="text-xs text-blue-600 font-medium">{internos}</span>
      </div>
    </div>
  );
}

function CanalRow({ canal, externos, internos, total, percentTotal }) {
  return (
    <TableRow>
      <TableCell className="font-medium">{canal}</TableCell>
      <TableCell className="text-center">
        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
          {formatNumber(externos)}
        </Badge>
      </TableCell>
      <TableCell className="text-center">
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          {formatNumber(internos)}
        </Badge>
      </TableCell>
      <TableCell className="text-center font-semibold">{formatNumber(total)}</TableCell>
      <TableCell className="text-center text-gray-500">{formatPercent(percentTotal)}</TableCell>
    </TableRow>
  );
}


// ==================== COMPONENTE PRINCIPAL ====================

export default function DashboardLeads() {
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [periodo, setPeriodo] = useState("todos");
  const [filtroInstituicao, setFiltroInstituicao] = useState("all");
  const [instituicoes, setInstituicoes] = useState([]);
  const [todasInstituicoes, setTodasInstituicoes] = useState([]);
  
  // Modal de importação
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importInstituicao, setImportInstituicao] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Estatísticas calculadas
  const [stats, setStats] = useState({
    total: 0,
    base: 0,
    externos: 0,
    internos: 0,
    porInstituicao: {},
    porCanalProspeccao: {},
    porServicoSolicitado: {},
    porCanal: {},
    porMes: {},
  });

  useEffect(() => {
    carregarDados();
    carregarInstituicoes();
  }, []);

  useEffect(() => {
    calcularEstatisticas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes, periodo, filtroInstituicao]);

  const carregarInstituicoes = async () => {
    try {
      const response = await axios.get(`${API}/api/lookup/instituicoes`);
      // A API retorna { instituicoes: ["001 - Nome", ...] }
      setTodasInstituicoes(response.data.instituicoes || response.data || []);
    } catch (error) {
      console.error("Erro ao carregar instituições:", error);
    }
  };

  const carregarDados = async () => {
    setLoading(true);
    try {
      // Obter empresa do contexto (multi-tenancy)
      const empresaAtual = JSON.parse(localStorage.getItem("empresa") || "null");
      const empresaParam = empresaAtual?.id ? `&empresa_id=${empresaAtual.id}` : "";
      
      // Buscar todos os clientes com paginação (limite de 100 por página)
      let todosClientes = [];
      let page = 1;
      let hasMore = true;
      const perPage = 100;
      
      while (hasMore) {
        const response = await axios.get(`${API}/api/clientes?per_page=${perPage}&page=${page}${empresaParam}`);
        const clientesData = response.data.clientes || [];
        todosClientes = [...todosClientes, ...clientesData];
        
        // Verificar se há mais páginas
        const total = response.data.total || response.data.total_clientes || 0;
        hasMore = todosClientes.length < total;
        page++;
        
        // Limite de segurança para evitar loops infinitos
        if (page > 20) break;
      }
      
      setClientes(todosClientes);
      
      // Extrair instituições únicas (tratando objetos)
      const instituicoesUnicas = [...new Set(todosClientes.map(c => {
        const inst = c.primeira_instituicao;
        if (typeof inst === 'object' && inst !== null) {
          return inst.nome || inst.instituicao || null;
        }
        return inst;
      }).filter(Boolean))];
      setInstituicoes(instituicoesUnicas.sort());
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const calcularEstatisticas = () => {
    if (clientes.length === 0) return;

    // EXCLUIR leads da base INSS que ainda não migraram (esteira_atual = BASE ou origem leads_base)
    // Eles só aparecem nos indicadores quando migrarem para EXTERNO/INTERNO ou forem para CAPTAÇÃO
    const clientesAtivos = clientes.filter(c => 
      c.esteira_atual !== "BASE" && c._fonte !== "leads_base"
    );

    // Filtrar por período
    const hoje = new Date();
    const clientesFiltrados = clientesAtivos.filter(c => {
      if (!c.created_at) return true;
      const dataCriacao = new Date(c.created_at);
      
      if (periodo === "mes_atual") {
        return dataCriacao.getMonth() === hoje.getMonth() && dataCriacao.getFullYear() === hoje.getFullYear();
      } else if (periodo === "ultimos_30") {
        const trintaDias = new Date(hoje.getTime() - 30 * 24 * 60 * 60 * 1000);
        return dataCriacao >= trintaDias;
      } else if (periodo === "ultimos_90") {
        const noventaDias = new Date(hoje.getTime() - 90 * 24 * 60 * 60 * 1000);
        return dataCriacao >= noventaDias;
      } else if (periodo === "ano_atual") {
        return dataCriacao.getFullYear() === hoje.getFullYear();
      }
      return true;
    });

    // Helper para extrair nome de instituição
    const getInstNome = (inst) => {
      if (!inst) return null;
      if (typeof inst === 'object') return inst.nome || inst.instituicao || null;
      return inst;
    };

    // Filtrar por instituição
    const clientesInstituicao = filtroInstituicao === "all" 
      ? clientesFiltrados 
      : clientesFiltrados.filter(c => getInstNome(c.primeira_instituicao) === filtroInstituicao);

    // Calcular totais
    const base = clientesInstituicao.filter(c => c.classificacao === "BASE").length;
    const externos = clientesInstituicao.filter(c => c.classificacao === "EXTERNO").length;
    const internos = clientesInstituicao.filter(c => c.classificacao === "INTERNO").length;

    // Agrupar por instituição
    const porInstituicao = {};
    clientesFiltrados.forEach(c => {
      const inst = getInstNome(c.primeira_instituicao) || "NÃO INFORMADO";
      if (!porInstituicao[inst]) {
        porInstituicao[inst] = { base: 0, externos: 0, internos: 0, total: 0 };
      }
      porInstituicao[inst].total++;
      if (c.classificacao === "BASE") {
        porInstituicao[inst].base++;
      } else if (c.classificacao === "EXTERNO") {
        porInstituicao[inst].externos++;
      } else {
        porInstituicao[inst].internos++;
      }
    });

    // Agrupar por canal de prospecção
    const porCanalProspeccao = {};
    clientesInstituicao.forEach(c => {
      const canal = c.canal_prospeccao || "NÃO INFORMADO";
      if (!porCanalProspeccao[canal]) {
        porCanalProspeccao[canal] = { base: 0, externos: 0, internos: 0, total: 0 };
      }
      porCanalProspeccao[canal].total++;
      if (c.classificacao === "BASE") {
        porCanalProspeccao[canal].base++;
      } else if (c.classificacao === "EXTERNO") {
        porCanalProspeccao[canal].externos++;
      } else {
        porCanalProspeccao[canal].internos++;
      }
    });

    // Agrupar por serviço solicitado
    const porServicoSolicitado = {};
    clientesInstituicao.forEach(c => {
      const servico = c.ultimo_servico_solicitado || "NÃO INFORMADO";
      if (!porServicoSolicitado[servico]) {
        porServicoSolicitado[servico] = { base: 0, externos: 0, internos: 0, total: 0 };
      }
      porServicoSolicitado[servico].total++;
      if (c.classificacao === "BASE") {
        porServicoSolicitado[servico].base++;
      } else if (c.classificacao === "EXTERNO") {
        porServicoSolicitado[servico].externos++;
      } else {
        porServicoSolicitado[servico].internos++;
      }
    });

    // Agrupar por canal (origem) - mantendo compatibilidade
    const porCanal = {};
    clientesInstituicao.forEach(c => {
      const canal = c.origem_dados || "NÃO INFORMADO";
      if (!porCanal[canal]) {
        porCanal[canal] = { externos: 0, internos: 0, total: 0 };
      }
      porCanal[canal].total++;
      if (c.classificacao === "EXTERNO") {
        porCanal[canal].externos++;
      } else {
        porCanal[canal].internos++;
      }
    });

    // Agrupar por mês
    const porMes = {};
    clientesInstituicao.forEach(c => {
      if (!c.created_at) return;
      const data = new Date(c.created_at);
      const mesAno = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`;
      if (!porMes[mesAno]) {
        porMes[mesAno] = { base: 0, externos: 0, internos: 0, total: 0 };
      }
      porMes[mesAno].total++;
      if (c.classificacao === "BASE") {
        porMes[mesAno].base++;
      } else if (c.classificacao === "EXTERNO") {
        porMes[mesAno].externos++;
      } else {
        porMes[mesAno].internos++;
      }
    });

    setStats({
      total: clientesInstituicao.length,
      base,
      externos,
      internos,
      porInstituicao,
      porCanalProspeccao,
      porServicoSolicitado,
      porCanal,
      porMes,
    });
  };

  // Função de importação de leads
  const handleImportLeads = async () => {
    if (!importFile) {
      toast.error("Selecione um arquivo para importar");
      return;
    }
    if (!importInstituicao) {
      toast.error("Selecione uma instituição");
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      formData.append("instituicao", importInstituicao);

      const response = await axios.post(`${API}/api/leads/importar`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const resultado = response.data;
      toast.success(`Importação concluída! ${resultado.importados || 0} leads importados.`);
      
      // Recarregar dados
      setImportModalOpen(false);
      setImportFile(null);
      setImportInstituicao("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      carregarDados();
    } catch (error) {
      console.error("Erro na importação:", error);
      toast.error(error.response?.data?.detail || "Erro ao importar leads");
    } finally {
      setImporting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ];
      if (!validTypes.includes(file.type) && !file.name.endsWith(".xlsx") && !file.name.endsWith(".xls") && !file.name.endsWith(".csv")) {
        toast.error("Formato inválido. Use arquivos Excel (.xlsx, .xls) ou CSV.");
        return;
      }
      setImportFile(file);
    }
  };

  const maxInstituicaoTotal = Math.max(...Object.values(stats.porInstituicao).map(s => s.total), 1);
  const totalCanais = Object.values(stats.porCanal).reduce((sum, c) => sum + c.total, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-leads-page">
      {/* Modal de Importação */}
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Importar Leads
            </DialogTitle>
            <DialogDescription>
              Importe leads de um arquivo Excel ou CSV para uma instituição específica.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Seleção de Instituição */}
            <div className="space-y-2">
              <Label htmlFor="instituicao">Instituição *</Label>
              <Select value={importInstituicao} onValueChange={setImportInstituicao}>
                <SelectTrigger data-testid="import-select-instituicao">
                  <SelectValue placeholder="Selecione a instituição" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {todasInstituicoes.map(inst => (
                    <SelectItem key={inst} value={inst}>
                      {inst}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Upload de Arquivo */}
            <div className="space-y-2">
              <Label htmlFor="file">Arquivo Excel/CSV *</Label>
              <div className="flex items-center gap-2">
                <Input
                  ref={fileInputRef}
                  id="file"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  className="flex-1"
                  data-testid="import-file-input"
                />
              </div>
              {importFile && (
                <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
                  <FileSpreadsheet className="h-4 w-4" />
                  <span>{importFile.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 ml-auto"
                    onClick={() => {
                      setImportFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            {/* Instruções */}
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
              <p className="font-medium mb-1">Formato esperado do arquivo:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Coluna &quot;CPF&quot; (obrigatório)</li>
                <li>Coluna &quot;Nome&quot; (obrigatório)</li>
                <li>Colunas opcionais: Telefone, Email, Endereço</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportModalOpen(false)} disabled={importing}>
              Cancelar
            </Button>
            <Button onClick={handleImportLeads} disabled={importing || !importFile || !importInstituicao} data-testid="import-submit-btn">
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            Dashboard de Leads
          </h1>
          <p className="text-gray-500">Gestão e análise de leads por instituição e origem</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-40" data-testid="select-periodo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mes_atual">Mês Atual</SelectItem>
              <SelectItem value="ultimos_30">Últimos 30 dias</SelectItem>
              <SelectItem value="ultimos_90">Últimos 90 dias</SelectItem>
              <SelectItem value="ano_atual">Ano Atual</SelectItem>
              <SelectItem value="todos">Todos</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filtroInstituicao} onValueChange={setFiltroInstituicao}>
            <SelectTrigger className="w-52" data-testid="select-instituicao">
              <SelectValue placeholder="Todas Instituições" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas Instituições</SelectItem>
              {instituicoes.map(inst => (
                <SelectItem key={inst} value={inst}>{inst}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={carregarDados} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          title="Total de Leads"
          value={formatNumber(stats.total)}
          subtitle={`${Object.keys(stats.porInstituicao).length} instituições`}
          icon={Users}
          color="bg-blue-500"
        />
        <StatCard
          title="Base"
          value={formatNumber(stats.base)}
          subtitle={`${formatPercent(stats.total > 0 ? (stats.base / stats.total) * 100 : 0)} do total (Importados)`}
          icon={Database}
          color="bg-gray-500"
        />
        <StatCard
          title="Externos"
          value={formatNumber(stats.externos)}
          subtitle={`${formatPercent(stats.total > 0 ? (stats.externos / stats.total) * 100 : 0)} do total (Prospectados)`}
          icon={UserPlus}
          color="bg-orange-500"
        />
        <StatCard
          title="Internos"
          value={formatNumber(stats.internos)}
          subtitle={`${formatPercent(stats.total > 0 ? (stats.internos / stats.total) * 100 : 0)} do total (Clientes)`}
          icon={UserCheck}
          color="bg-green-500"
        />
        <StatCard
          title="Taxa Conversão"
          value={formatPercent(stats.total > 0 ? (stats.internos / stats.total) * 100 : 0)}
          subtitle="Internos / Total"
          icon={TrendingUp}
          color="bg-purple-500"
        />
      </div>

      {/* Gráficos e Tabelas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads por Instituição */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Leads por Instituição
            </CardTitle>
            <CardDescription>Distribuição de leads por instituição e classificação</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-gray-400"></div>
                <span>Base (Importados)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-orange-500"></div>
                <span>Externos (Prospectados)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-blue-500"></div>
                <span>Internos (Clientes)</span>
              </div>
            </div>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {Object.entries(stats.porInstituicao)
                .sort((a, b) => b[1].total - a[1].total)
                .slice(0, 15)
                .map(([instituicao, dados]) => (
                  <InstituicaoBar
                    key={instituicao}
                    instituicao={instituicao}
                    base={dados.base}
                    externos={dados.externos}
                    internos={dados.internos}
                    total={dados.total}
                    maxTotal={maxInstituicaoTotal}
                  />
                ))}
              {Object.keys(stats.porInstituicao).length === 0 && (
                <p className="text-center text-gray-500 py-8">Nenhum dado disponível</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Leads por Canal de Prospecção */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Canais de Prospecção
            </CardTitle>
            <CardDescription>Como os leads chegaram até a empresa</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-orange-600 font-medium">Ext</span>
                <span className="text-gray-400">=</span>
                <span className="text-xs">Externos (Novos)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blue-600 font-medium">Int</span>
                <span className="text-gray-400">=</span>
                <span className="text-xs">Internos (Clientes)</span>
              </div>
            </div>
            <div className="space-y-1 max-h-[350px] overflow-y-auto">
              {(() => {
                const maxCanalTotal = Math.max(...Object.values(stats.porCanalProspeccao).map(s => s.total), 1);
                return Object.entries(stats.porCanalProspeccao)
                  .sort((a, b) => b[1].total - a[1].total)
                  .slice(0, 12)
                  .map(([canal, dados]) => (
                    <CanalProspeccaoBar
                      key={canal}
                      canal={canal}
                      externos={dados.externos}
                      internos={dados.internos}
                      total={dados.total}
                      maxTotal={maxCanalTotal}
                    />
                  ));
              })()}
              {Object.keys(stats.porCanalProspeccao).length === 0 && (
                <p className="text-center text-gray-500 py-8">Nenhum dado disponível</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabelas Detalhadas em Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tabela por Canal de Prospecção */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Detalhamento por Canal de Prospecção</CardTitle>
            <CardDescription>Quantidade de leads por canal com separação interno/externo</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Canal</TableHead>
                  <TableHead className="text-center">Externos</TableHead>
                  <TableHead className="text-center">Internos</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead className="text-center">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const totalCanalProspeccao = Object.values(stats.porCanalProspeccao).reduce((sum, c) => sum + c.total, 0);
                  return Object.entries(stats.porCanalProspeccao)
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([canal, dados]) => (
                      <CanalRow
                        key={canal}
                        canal={canal}
                        externos={dados.externos}
                        internos={dados.internos}
                        total={dados.total}
                        percentTotal={totalCanalProspeccao > 0 ? (dados.total / totalCanalProspeccao) * 100 : 0}
                      />
                    ));
                })()}
                {Object.keys(stats.porCanalProspeccao).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      Nenhum dado disponível
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Tabela por Serviço Solicitado */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Entrada de Leads por Serviço Solicitado</CardTitle>
            <CardDescription>Tipo de serviço solicitado pelos leads</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Serviço</TableHead>
                  <TableHead className="text-center">Base</TableHead>
                  <TableHead className="text-center">Externos</TableHead>
                  <TableHead className="text-center">Internos</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead className="text-center">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const totalServico = Object.values(stats.porServicoSolicitado || {}).reduce((sum, c) => sum + c.total, 0);
                  const servicoLabels = {
                    "SIMULACAO_CONSIGNADO": "💰 Simulação Consignado",
                    "ANDAMENTO_CONTRATO": "📑 Andamento do Contrato",
                    "OUTROS_ASSUNTOS": "✍🏽 Outros Assuntos",
                    "NÃO INFORMADO": "❓ Não Informado"
                  };
                  return Object.entries(stats.porServicoSolicitado || {})
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([servico, dados]) => (
                      <TableRow key={servico}>
                        <TableCell className="font-medium">{servicoLabels[servico] || servico}</TableCell>
                        <TableCell className="text-center text-gray-600">{dados.base}</TableCell>
                        <TableCell className="text-center text-orange-600 font-medium">{dados.externos}</TableCell>
                        <TableCell className="text-center text-blue-600 font-medium">{dados.internos}</TableCell>
                        <TableCell className="text-center font-semibold">{dados.total}</TableCell>
                        <TableCell className="text-center text-gray-500">
                          {formatPercent(totalServico > 0 ? (dados.total / totalServico) * 100 : 0)}
                        </TableCell>
                      </TableRow>
                    ));
                })()}
                {Object.keys(stats.porServicoSolicitado || {}).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      Nenhum dado disponível
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Segunda linha de tabelas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tabela por Origem dos Dados */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Entradas por Origem dos Dados</CardTitle>
            <CardDescription>Origem dos leads separados por tipo</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Origem</TableHead>
                  <TableHead className="text-center">Externos</TableHead>
                  <TableHead className="text-center">Internos</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead className="text-center">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(stats.porCanal)
                  .sort((a, b) => b[1].total - a[1].total)
                  .slice(0, 10)
                  .map(([canal, dados]) => (
                    <CanalRow
                      key={canal}
                      canal={canal}
                      externos={dados.externos}
                      internos={dados.internos}
                      total={dados.total}
                      percentTotal={totalCanais > 0 ? (dados.total / totalCanais) * 100 : 0}
                    />
                  ))}
                {Object.keys(stats.porCanal).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      Nenhum dado disponível
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Tabela Detalhada por Instituição */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Resumo Detalhado por Instituição</CardTitle>
          <CardDescription>Quantidade de leads por instituição com separação interno/externo</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Instituição</TableHead>
                <TableHead className="text-center">Leads Externos (Novos)</TableHead>
                <TableHead className="text-center">Leads Internos (Clientes)</TableHead>
                <TableHead className="text-center">Total</TableHead>
                <TableHead className="text-center">% Externos</TableHead>
                <TableHead className="text-center">% Internos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(stats.porInstituicao)
                .sort((a, b) => b[1].total - a[1].total)
                .map(([instituicao, dados]) => (
                  <TableRow key={instituicao}>
                    <TableCell className="font-medium">{instituicao}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                        {formatNumber(dados.externos)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {formatNumber(dados.internos)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center font-semibold">{formatNumber(dados.total)}</TableCell>
                    <TableCell className="text-center text-orange-600">
                      {formatPercent(dados.total > 0 ? (dados.externos / dados.total) * 100 : 0)}
                    </TableCell>
                    <TableCell className="text-center text-blue-600">
                      {formatPercent(dados.total > 0 ? (dados.internos / dados.total) * 100 : 0)}
                    </TableCell>
                  </TableRow>
                ))}
              {/* Linha de Total */}
              <TableRow className="bg-gray-50 font-semibold">
                <TableCell>Total Geral</TableCell>
                <TableCell className="text-center text-orange-700">{formatNumber(stats.externos)}</TableCell>
                <TableCell className="text-center text-blue-700">{formatNumber(stats.internos)}</TableCell>
                <TableCell className="text-center">{formatNumber(stats.total)}</TableCell>
                <TableCell className="text-center text-orange-600">
                  {formatPercent(stats.total > 0 ? (stats.externos / stats.total) * 100 : 0)}
                </TableCell>
                <TableCell className="text-center text-blue-600">
                  {formatPercent(stats.total > 0 ? (stats.internos / stats.total) * 100 : 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Evolução Mensal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Evolução Mensal de Leads
          </CardTitle>
          <CardDescription>Acompanhamento do volume de entradas ao longo do tempo</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mês/Ano</TableHead>
                <TableHead className="text-center">Leads Externos (Novos)</TableHead>
                <TableHead className="text-center">Leads Internos (Clientes)</TableHead>
                <TableHead className="text-center">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(stats.porMes)
                .sort((a, b) => b[0].localeCompare(a[0]))
                .slice(0, 12)
                .map(([mesAno, dados]) => {
                  const [ano, mes] = mesAno.split("-");
                  const meses = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
                  const mesNome = meses[parseInt(mes) - 1];
                  return (
                    <TableRow key={mesAno}>
                      <TableCell className="font-medium">{mesNome}/{ano}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                          {formatNumber(dados.externos)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {formatNumber(dados.internos)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center font-semibold">{formatNumber(dados.total)}</TableCell>
                    </TableRow>
                  );
                })}
              {Object.keys(stats.porMes).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                    Nenhum dado disponível
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
